import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { Circle, CircleCheck, CircleX, Download, Info, LoaderCircle, RotateCcw, ScrollText, TriangleAlert, type LucideIcon } from 'lucide-react'
import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'
import { z } from 'zod'

import {
  adminAddHomeNetworkMutation,
  adminDeleteDeviceMutation,
  adminDeleteHomeNetworkMutation,
  adminDeviceDetailOptions,
  adminDeviceDetailQueryKey,
  adminListCommandsOptions,
  adminListCommandsQueryKey,
  adminListHomeNetworksOptions,
  adminListHomeNetworksQueryKey,
  adminListModuleRegistryOptions,
  adminListModulesOptions,
  adminListModulesQueryKey,
  adminQueueCommandMutation,
  adminSetModuleMutation,
  adminSetTargetVersionMutation,
  adminUpdateDeviceMutation,
} from '@/client/@tanstack/react-query.gen'
import type { DeviceModuleRead, DeviceRead, ModuleSpecRead } from '@/client'
import { DeviceOnlineIndicator } from '@/components/features/device-online-indicator'
import { FilteringRules } from '@/components/features/filtering-rules'
import { RecentScreenshots } from '@/components/features/recent-screenshots'
import { ScreenshotGallery } from '@/components/features/screenshot-gallery'
import { SubmissionLog } from '@/components/features/submission-log'
import { WindowCaptureList } from '@/components/features/window-capture-list'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { commandPresets, resolveCommandPresetConfig } from '@/lib/command-presets'
import { commandStatusTone, permissionsNeedingAction, type CommandStatusTone, type TccPermission } from '@/lib/device-telemetry'
import { ADMIN_COMMANDS_POLL_MS, ADMIN_DEVICE_POLL_MS } from '@/lib/polling'
import { cn } from '@/lib/utils'

const deviceSchema = z.object({
  name: z.string().min(1, 'Required'),
  serial_number: z.string().min(1, 'Required'),
})
type DeviceValues = z.infer<typeof deviceSchema>

const TCC_PERMISSION_LABELS: Record<TccPermission, string> = {
  screen_recording: 'Screen recording',
  accessibility: 'Accessibility',
}

const TCC_PERMISSION_ORDER: readonly TccPermission[] = [
  'screen_recording',
  'accessibility',
]

function tccGrantLabel(granted: boolean | null): string {
  if (granted === null) {
    return '—'
  }
  return granted ? 'Granted' : 'Not granted'
}

function captureAgentLabel(running: boolean | null): string {
  if (running === null) {
    return 'Unknown'
  }
  return running ? 'Running' : 'Not running'
}

function mdmEnrollmentLabel(enrolled: boolean | null): string {
  if (enrolled === true) {
    return 'Enrolled'
  }
  if (enrolled === false) {
    return 'Not enrolled'
  }
  return 'Unknown'
}

function relativeTime(value: string | null): string {
  return value ? formatDistanceToNow(new Date(value), { addSuffix: true }) : 'Never'
}


const COMMAND_STATUS_ICON: Record<
  CommandStatusTone,
  { Icon: LucideIcon; className: string; label: string }
> = {
  completed: { Icon: CircleCheck, className: 'text-green-500', label: 'Completed' },
  failed: { Icon: CircleX, className: 'text-red-500', label: 'Failed' },
  timeout: { Icon: TriangleAlert, className: 'text-yellow-500', label: 'Timed out' },
  delivered: { Icon: LoaderCircle, className: 'text-muted-foreground animate-spin', label: 'Running' },
  other: { Icon: Circle, className: 'text-muted-foreground/40', label: 'Pending' },
}

function CommandStatusIcon({ status }: { status: string }) {
  const { Icon, className, label } = COMMAND_STATUS_ICON[commandStatusTone(status)]
  return (
    <span role="img" aria-label={label} className="inline-flex">
      <Icon aria-hidden className={cn('h-4 w-4 shrink-0', className)} />
    </span>
  )
}

const PRESET_ICONS: Record<string, LucideIcon> = {
  'Restart Capture': RotateCcw,
  'Get Logs': ScrollText,
  'Reinstall Capture': Download,
  'System Info': Info,
}

function TelemetryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b py-1.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  )
}

function EditDeviceDialog({
  familyId,
  deviceId,
  device,
  onDone,
}: {
  familyId: string
  deviceId: string
  device: DeviceRead
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const form = useForm<DeviceValues>({
    resolver: zodResolver(deviceSchema),
    defaultValues: { name: device.name, serial_number: device.serial_number },
  })
  const update = useMutation({
    ...adminUpdateDeviceMutation(),
    onSuccess: () => {
      onDone()
      setOpen(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit device</DialogTitle>
          <DialogDescription>Rename the device or correct its serial number.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) =>
              update.mutate({ path: { family_id: familyId, device_id: deviceId }, body: values }),
            )}
            noValidate
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="serial_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Serial number</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function ModuleRow({
  deviceId,
  spec,
  deviceModule,
  onSaved,
}: {
  deviceId: string
  spec: ModuleSpecRead
  deviceModule: DeviceModuleRead | undefined
  onSaved: () => void
}) {
  const [enabled, setEnabled] = useState(deviceModule?.enabled ?? spec.default_enabled)
  const [configText, setConfigText] = useState(() =>
    JSON.stringify({ ...spec.default_config, ...(deviceModule?.config ?? {}) }, null, 2),
  )
  const [jsonError, setJsonError] = useState<string | null>(null)
  const save = useMutation({ ...adminSetModuleMutation(), onSuccess: onSaved })

  const submit = (): void => {
    let config: Record<string, unknown>
    try {
      config = JSON.parse(configText) as Record<string, unknown>
    } catch {
      setJsonError('Config must be valid JSON.')
      return
    }
    setJsonError(null)
    save.mutate({
      path: { device_id: deviceId },
      body: { module_name: spec.name, enabled, config },
    })
  }

  return (
    <li className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-mono">{spec.name}</span>
          <span className="text-muted-foreground ml-2 text-xs">{spec.module_class}</span>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            aria-label={`Enable ${spec.name}`}
            className="size-4"
          />
          <span className="text-muted-foreground">Enabled</span>
        </label>
      </div>
      <label className="block text-sm">
        <span className="text-muted-foreground">Config (JSON)</span>
        <Textarea
          value={configText}
          onChange={(e) => setConfigText(e.target.value)}
          aria-label={`${spec.name} config`}
          rows={3}
          className="font-mono text-xs"
        />
      </label>
      {jsonError && (
        <p className="text-destructive text-sm" role="alert">
          {jsonError}
        </p>
      )}
      <Button
        type="button"
        size="sm"
        onClick={submit}
        disabled={save.isPending}
        aria-label={`Save ${spec.name}`}
      >
        Save
      </Button>
    </li>
  )
}

function TargetVersionControl({
  deviceId,
  targetVersion,
  onSaved,
}: {
  deviceId: string
  targetVersion: string | null
  onSaved: () => void
}) {
  const [value, setValue] = useState(targetVersion ?? '')
  const save = useMutation({ ...adminSetTargetVersionMutation(), onSuccess: onSaved })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        const trimmed = value.trim()
        save.mutate({
          path: { device_id: deviceId },
          body: { target_version: trimmed === '' ? null : trimmed },
        })
      }}
      className="flex flex-wrap items-end gap-2"
    >
      <label className="text-sm">
        <span className="text-muted-foreground mr-2">Target version</span>
        <Input
          name="target_version"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Follow fleet default"
          className="inline-block w-40"
        />
      </label>
      <Button type="submit" size="sm" disabled={save.isPending}>
        Update target version
      </Button>
    </form>
  )
}


export function AdminDeviceDetail() {
  const { familyId = '', deviceId = '' } = useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const device = useQuery({
    ...adminDeviceDetailOptions({ path: { family_id: familyId, device_id: deviceId } }),
    refetchInterval: ADMIN_DEVICE_POLL_MS,
  })
  const refetch = useCallback((): void => {
    void queryClient.invalidateQueries({
      queryKey: adminDeviceDetailQueryKey({ path: { family_id: familyId, device_id: deviceId } }),
    })
  }, [queryClient, familyId, deviceId])
  const remove = useMutation({
    ...adminDeleteDeviceMutation(),
    onSuccess: () => navigate(`/admin/families/${familyId}`),
  })

  const modules = useQuery(adminListModulesOptions({ path: { device_id: deviceId } }))
  const registry = useQuery(adminListModuleRegistryOptions())
  const modulesKey = adminListModulesQueryKey({ path: { device_id: deviceId } })
  const networks = useQuery(adminListHomeNetworksOptions({ path: { device_id: deviceId } }))
  const commands = useQuery({
    ...adminListCommandsOptions({ path: { device_id: deviceId } }),
    refetchInterval: ADMIN_COMMANDS_POLL_MS,
  })
  const invalidate = (queryKey: readonly unknown[]) => (): void => {
    void queryClient.invalidateQueries({ queryKey })
  }
  const setModule = useMutation({
    ...adminSetModuleMutation(),
    onSuccess: invalidate(adminListModulesQueryKey({ path: { device_id: deviceId } })),
  })
  const networksKey = adminListHomeNetworksQueryKey({ path: { device_id: deviceId } })
  const addNetwork = useMutation({ ...adminAddHomeNetworkMutation(), onSuccess: invalidate(networksKey) })
  const removeNetwork = useMutation({
    ...adminDeleteHomeNetworkMutation(),
    onSuccess: invalidate(networksKey),
  })
  const queueCommand = useMutation({
    ...adminQueueCommandMutation(),
    onSuccess: invalidate(adminListCommandsQueryKey({ path: { device_id: deviceId } })),
  })
  const [networkLabel, setNetworkLabel] = useState('')
  const presets = useMemo(() => commandPresets(resolveCommandPresetConfig()), [])

  if (!device.data) {
    return <p className="text-muted-foreground">Loading…</p>
  }
  const d = device.data
  const needsParentAction = permissionsNeedingAction({
    screen_recording: d.tcc_screen_recording_misses,
    accessibility: d.tcc_accessibility_misses,
  })
  const tccGranted: Record<TccPermission, boolean | null> = {
    screen_recording: d.tcc_screen_recording,
    accessibility: d.tcc_accessibility,
  }
  const reportedMac = d.last_gateway_mac
  const alreadyRecorded =
    !!reportedMac && (networks.data ?? []).some((net) => net.gateway_mac === reportedMac)
  const screenshotsModule = modules.data?.find((m) => m.module_name === 'screenshots')
  const windowCaptureModule = modules.data?.find((m) => m.module_name === 'window_capture')

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{d.name}</h1>
          <div className="text-muted-foreground mt-1 flex flex-wrap gap-x-3 text-sm">
            <span>{d.serial_number}</span>
            <span className="capitalize">{d.status}</span>
            {d.os_version && <span>macOS {d.os_version}</span>}
            {d.agent_version && <span>Agent {d.agent_version}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <RecentScreenshots deviceId={deviceId} />
          <EditDeviceDialog
            familyId={familyId}
            deviceId={deviceId}
            device={d}
            onDone={refetch}
          />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm">
                Delete device
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {d.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes the device and all of its captured data.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    remove.mutate({ path: { family_id: familyId, device_id: deviceId } })
                  }
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>


      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Telemetry</h2>
        {needsParentAction.length > 0 && (
          <Badge variant="destructive">
            {`Needs parent action: ${needsParentAction
              .map((permission) => TCC_PERMISSION_LABELS[permission])
              .join(', ')}`}
          </Badge>
        )}
        {d.mdm_enrolled === false && (
          <Badge
            variant="outline"
            className="border-destructive text-destructive"
            data-testid="mdm-enrollment-incident"
          >
            <TriangleAlert aria-hidden />
            Operator incident: unexpected MDM unenrollment
          </Badge>
        )}
        <dl className="grid grid-cols-1 gap-x-8 text-sm sm:grid-cols-2">
          <TelemetryRow label="Liveness">
            <DeviceOnlineIndicator
              lastHeartbeatAt={d.last_heartbeat_at}
              userAgentHeartbeatAt={d.last_user_agent_heartbeat_at}
            />
          </TelemetryRow>
          <TelemetryRow label="Daemon heartbeat">{relativeTime(d.last_heartbeat_at)}</TelemetryRow>
          <TelemetryRow label="User-agent heartbeat">
            {relativeTime(d.last_user_agent_heartbeat_at)}
          </TelemetryRow>
          <TelemetryRow label="Capture agent">
            {captureAgentLabel(d.capture_agent_running)}
          </TelemetryRow>
          <TelemetryRow label="MDM enrollment">
            <span data-testid="mdm-enrollment-status">{mdmEnrollmentLabel(d.mdm_enrolled)}</span>
          </TelemetryRow>
          <TelemetryRow label="MDM enrollment observed">
            {relativeTime(d.mdm_enrolled_observed_at)}
          </TelemetryRow>
          {TCC_PERMISSION_ORDER.map((permission) => {
            const granted = tccGranted[permission]
            return (
              <TelemetryRow key={permission} label={TCC_PERMISSION_LABELS[permission]}>
                <span data-testid={`tcc-${permission}`}>{tccGrantLabel(granted)}</span>
              </TelemetryRow>
            )
          })}
          <TelemetryRow label="Last gateway MAC">{d.last_gateway_mac ?? '—'}</TelemetryRow>
          <TelemetryRow label="Registered">
            {new Date(d.created_at).toLocaleDateString()}
          </TelemetryRow>
        </dl>
      </section>


      <FilteringRules deviceId={deviceId} />

      <SubmissionLog deviceId={deviceId} />

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Target version</h2>
        <TargetVersionControl deviceId={deviceId} targetVersion={d.target_version} onSaved={refetch} />
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Capture intervals</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            const data = new FormData(e.currentTarget)
            setModule.mutate({
              path: { device_id: deviceId },
              body: {
                module_name: 'screenshots',
                enabled: screenshotsModule?.enabled ?? true,
                config: { interval_seconds: Number(data.get('screen_interval')) },
              },
            })
            setModule.mutate({
              path: { device_id: deviceId },
              body: {
                module_name: 'window_capture',
                enabled: windowCaptureModule?.enabled ?? true,
                config: { interval_seconds: Number(data.get('window_interval')) },
              },
            })
          }}
          className="flex flex-wrap items-end gap-2"
        >
          <label className="text-sm">
            <span className="text-muted-foreground mr-2">Screenshot (seconds)</span>
            <Input
              name="screen_interval"
              type="number"
              defaultValue={(screenshotsModule?.config.interval_seconds as number | undefined) ?? 60}
              className="inline-block w-28"
            />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground mr-2">Window capture (seconds)</span>
            <Input
              name="window_interval"
              type="number"
              defaultValue={(windowCaptureModule?.config.interval_seconds as number | undefined) ?? 60}
              className="inline-block w-28"
            />
          </label>
          <Button type="submit" size="sm" disabled={setModule.isPending}>
            Update intervals
          </Button>
        </form>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Modules</h2>
        {registry.data?.length ? (
          <ul className="space-y-3">
            {registry.data.map((spec) => (
              <ModuleRow
                key={spec.name}
                deviceId={deviceId}
                spec={spec}
                deviceModule={modules.data?.find((m) => m.module_name === spec.name)}
                onSaved={invalidate(modulesKey)}
              />
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">No modules in the registry.</p>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Home networks</h2>
        {networks.data?.length ? (
          <ul className="space-y-1">
            {networks.data.map((net) => (
              <li key={net.gateway_mac} className="flex items-center justify-between text-sm">
                <span>
                  <span className="font-mono">{net.gateway_mac}</span> · {net.label}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    removeNetwork.mutate({
                      path: { device_id: deviceId },
                      query: { gateway_mac: net.gateway_mac },
                    })
                  }
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">No home networks.</p>
        )}
        {reportedMac ? (
          <div className="space-y-2 text-sm">
            <p>
              Last-reported gateway MAC: <span className="font-mono">{reportedMac}</span>
              {alreadyRecorded && <span className="text-muted-foreground"> (already recorded)</span>}
            </p>
            {!alreadyRecorded && (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  addNetwork.mutate({
                    path: { device_id: deviceId },
                    body: { gateway_mac: reportedMac, label: networkLabel },
                  })
                  setNetworkLabel('')
                }}
                className="flex items-end gap-2"
              >
                <label className="text-sm">
                  <span className="text-muted-foreground mr-2">Label</span>
                  <Input
                    name="label"
                    value={networkLabel}
                    onChange={(e) => setNetworkLabel(e.target.value)}
                    className="inline-block w-32"
                  />
                </label>
                <Button
                  type="submit"
                  size="sm"
                  disabled={networkLabel.trim().length === 0 || addNetwork.isPending}
                >
                  Add as home network
                </Button>
              </form>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            No gateway MAC reported yet — wait for the next user-agent heartbeat.
          </p>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Commands</h2>
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => {
            const Icon = PRESET_ICONS[preset.label]
            return (
              <Button
                key={preset.label}
                type="button"
                variant="outline"
                size="sm"
                disabled={queueCommand.isPending}
                onClick={() =>
                  queueCommand.mutate({
                    path: { device_id: deviceId },
                    body: {
                      type: 'run-script',
                      payload: { script: preset.script, timeout: preset.timeout },
                    },
                  })
                }
              >
                <Icon aria-hidden />
                {preset.label}
              </Button>
            )
          })}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            const fd = new FormData(e.currentTarget)
            queueCommand.mutate({
              path: { device_id: deviceId },
              body: {
                type: 'run-script',
                payload: { script: String(fd.get('script')), timeout: Number(fd.get('timeout')) },
              },
            })
            e.currentTarget.reset()
          }}
          className="space-y-2"
        >
          <label className="block text-sm">
            <span className="text-muted-foreground">Script</span>
            <Textarea name="script" rows={3} />
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground mr-2">Timeout (s)</span>
            <Input name="timeout" type="number" defaultValue={30} className="inline-block w-24" />
          </label>
          <div>
            <Button type="submit" size="sm" disabled={queueCommand.isPending}>
              Run script
            </Button>
          </div>
        </form>
        {commands.data?.length ? (
          <ul className="space-y-2 text-sm">
            {commands.data.map((cmd) => (
              <li key={cmd.id} className="space-y-1 rounded-md border p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    <CommandStatusIcon status={cmd.status} />
                    <span className="font-mono">{cmd.type}</span>
                  </span>
                  <span className="text-muted-foreground capitalize">{cmd.status}</span>
                </div>
                {cmd.exit_code !== null && (
                  <p className="text-muted-foreground text-xs">{`Exit code: ${cmd.exit_code}`}</p>
                )}
                {cmd.stdout && (
                  <div>
                    <p className="text-muted-foreground text-xs">stdout</p>
                    <pre className="bg-muted overflow-x-auto rounded p-2 font-mono text-xs whitespace-pre-wrap">
                      {cmd.stdout}
                    </pre>
                  </div>
                )}
                {cmd.stderr && (
                  <div>
                    <p className="text-muted-foreground text-xs">stderr</p>
                    <pre className="bg-muted overflow-x-auto rounded p-2 font-mono text-xs whitespace-pre-wrap">
                      {cmd.stderr}
                    </pre>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">No commands yet.</p>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Captures</h2>
        <Tabs defaultValue="screenshots">
          <TabsList>
            <TabsTrigger value="screenshots">Screenshots</TabsTrigger>
            <TabsTrigger value="window-captures">Window captures</TabsTrigger>
          </TabsList>
          <TabsContent value="screenshots">
            <ScreenshotGallery deviceId={deviceId} />
          </TabsContent>
          <TabsContent value="window-captures">
            <WindowCaptureList deviceId={deviceId} />
          </TabsContent>
        </Tabs>
      </section>
    </div>
  )
}
